/**
 * MessageView Component
 * 
 * Displays messages for a selected conversation.
 * Features:
 * - Message list with grouping
 * - Date separators
 * - ALWAYS starts at bottom (newest messages)
 * - Infinite scroll (load more when scrolling up)
 * - "New messages" floating button when scrolled up
 * - Pending/failed messages with retry
 * - Empty state for new conversations
 * - Search: scroll to message and highlight
 * - Search navigation (prev/next)
 */

import React, { useEffect, useRef, useCallback, useState, useMemo, useLayoutEffect } from 'react';
import { Loader2, MessageSquare, ChevronDown, ChevronUp, X, Search } from 'lucide-react';
import { useMessages } from './hooks/useMessages';
import MessageBubble from './MessageBubble';
import DateSeparator from './DateSeparator';
import { CONVERSATION_TYPES } from '@/lib/firestore';
import { Button } from '@/components/ui/button';
import { stripHtml } from './utils/htmlUtils';

// Threshold for considering "at bottom" (in pixels)
const SCROLL_BOTTOM_THRESHOLD = 100;
// Threshold for triggering load more (in pixels from top)
const SCROLL_TOP_THRESHOLD = 200;

export default function MessageView({
  conversation,
  currentUserId,
  currentUser,
  pendingMessages = [],
  onRetryMessage,
  onCancelMessage,
  // Search props
  searchTargetMessage = null,
  searchQuery = '',
  onClearSearch,
  // Callback when highlight animation has been consumed (prevents re-highlighting on remount)
  onHighlightConsumed,
  // Notification highlight props (separate from search)
  highlightMessageId = null,
  onHighlightComplete,
  // Dynamic composer height for mobile scroll padding
  composerHeight = 0,
  onReply,
}) {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageRefs = useRef({});
  const prevScrollHeightRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  
  // Track if we've done initial scroll for this conversation
  const hasScrolledToBottomRef = useRef(false);
  const currentConversationIdRef = useRef(null);
  
  // Track scroll position state
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [showNewMessageButton, setShowNewMessageButton] = useState(false);
  
  // Search highlighting state
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [navigatedMatchIndex, setNavigatedMatchIndex] = useState(0);
  
  // Track which message we've already highlighted (to prevent re-highlighting on message updates)
  const lastHighlightedTargetRef = useRef(null);

  const {
    messages,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMoreMessages
  } = useMessages(conversation?.id, currentUserId, conversation?.participants || []);

  const isGroupChat = conversation?.type === CONVERSATION_TYPES.GROUP;

  // Saved scroll positions per conversation (persists across conversation switches)
  const scrollPositionsRef = useRef({});

  // Unified message stream: merge pending messages into the main messages array.
  // When onSnapshot confirms a pending message, it's filtered out and the real one
  // takes its place — same render cycle, same array length, zero layout shift.
  const unifiedMessages = useMemo(() => {
    const confirmedIds = new Set(messages.map(m => m.id));
    const activePending = pendingMessages.filter(pm =>
      !pm.firestoreId || !confirmedIds.has(pm.firestoreId)
    );

    if (activePending.length === 0) return messages;

    const lastMsg = messages[messages.length - 1];
    const lastIsSameUser = lastMsg && lastMsg.senderId === currentUserId;

    const pendingAsMessages = activePending.map((pm, idx) => ({
      id: pm.tempId,
      text: pm.text || '',
      senderId: currentUserId,
      type: pm.type || 'text',
      images: pm.images || [],
      voiceMemo: pm.voiceMemo || null,
      gifUrl: pm.gifUrl || null,
      poll: pm.poll || null,
      createdAt: pm.createdAt || new Date(),
      status: pm.status === 'failed' ? 'failed' : 'sending',
      _pending: true,
      _tempId: pm.tempId,
      _error: pm.error,
      readBy: [],
      reactions: {},
      isOwnMessage: true,
      showDateSeparator: false,
      isFirstInGroup: idx === 0 && !lastIsSameUser,
      isLastInGroup: idx === activePending.length - 1,
      showSenderName: false,
    }));

    return [...messages, ...pendingAsMessages];
  }, [messages, pendingMessages, currentUserId]);

  // Save scroll position before switching away from a conversation
  const saveScrollPosition = useCallback((convId) => {
    if (!convId || !messagesContainerRef.current) return;
    const container = messagesContainerRef.current;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    scrollPositionsRef.current[convId] = {
      distanceFromBottom,
      isAtBottom: distanceFromBottom < 50
    };
  }, []);

  // Reset when conversation changes
  useEffect(() => {
    const prevId = currentConversationIdRef.current;
    if (conversation?.id !== prevId) {
      // Save scroll position of the conversation we're leaving
      saveScrollPosition(prevId);
      
      currentConversationIdRef.current = conversation?.id;
      hasScrolledToBottomRef.current = false;
      isLoadingMoreRef.current = false;
      prevScrollHeightRef.current = 0;
      lastHighlightedTargetRef.current = null;
      setIsAtBottom(true);
      setNewMessageCount(0);
      setShowNewMessageButton(false);
      setHighlightedMessageId(null);
      setNavigatedMatchIndex(0);
      messageRefs.current = {};
    }
  }, [conversation?.id, saveScrollPosition]);

  // Reset highlight tracking when searchTargetMessage is cleared
  useEffect(() => {
    if (!searchTargetMessage) {
      lastHighlightedTargetRef.current = null;
    }
  }, [searchTargetMessage]);

  // CRITICAL: Scroll to bottom (or saved position) when messages first load for a conversation
  // Using useLayoutEffect to run synchronously after DOM updates
  useLayoutEffect(() => {
    // Only scroll if:
    // 1. Not loading
    // 2. We have messages
    // 3. We haven't scrolled yet for this conversation
    // 4. Not coming from a search (searchTargetMessage would scroll to specific message)
    if (!loading && messages.length > 0 && !hasScrolledToBottomRef.current && !searchTargetMessage) {
      const container = messagesContainerRef.current;
      if (container) {
        const saved = scrollPositionsRef.current[conversation?.id];
        if (saved && !saved.isAtBottom) {
          // Restore saved scroll position (distance from bottom)
          container.scrollTop = container.scrollHeight - container.clientHeight - saved.distanceFromBottom;
          setIsAtBottom(false);
        } else {
          // Scroll to bottom
          container.scrollTop = container.scrollHeight;
        }
        hasScrolledToBottomRef.current = true;
      }
    }
  }, [loading, messages.length, searchTargetMessage, conversation?.id]);

  // Additional scroll after a brief delay to handle any async content (images, etc.)
  // Only run ONCE after initial scroll to catch layout shifts from async image loading
  useEffect(() => {
    if (!loading && messages.length > 0 && hasScrolledToBottomRef.current && !searchTargetMessage) {
      const container = messagesContainerRef.current;
      if (container) {
        const timeoutId = setTimeout(() => {
          // Skip if user has scrolled away from bottom (they're browsing history)
          const { scrollTop, scrollHeight, clientHeight } = container;
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          
          // Only auto-scroll if we're very close to bottom (user hasn't scrolled up)
          if (distanceFromBottom < 100) {
            container.scrollTop = scrollHeight;
          }
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, messages.length, searchTargetMessage]); // Only depend on length, not the full messages array

  // Find all messages matching the search query
  const matchingMessages = useMemo(() => {
    if (!(searchQuery || '').trim()) return [];
    const lowerQuery = searchQuery.toLowerCase();
    return messages.filter(msg => 
      msg.text && stripHtml(msg.text).toLowerCase().includes(lowerQuery)
    ).map(msg => msg.id);
  }, [messages, searchQuery]);

  const searchMatchIds = matchingMessages;

  // Scroll to and highlight target message when it changes (from search/notification)
  useEffect(() => {
    if (!searchTargetMessage || messages.length === 0) return;
    
    // Don't re-highlight if we've already highlighted this message
    if (lastHighlightedTargetRef.current === searchTargetMessage.id) return;

    const targetExists = messages.some(m => m.id === searchTargetMessage.id);
    
    if (targetExists) {
      // Mark this target as highlighted
      lastHighlightedTargetRef.current = searchTargetMessage.id;
      
      // Notify parent that highlight has been consumed (prevents re-highlighting on tab switch/remount)
      if (onHighlightConsumed) onHighlightConsumed();
      
      setTimeout(() => {
        const messageElement = messageRefs.current[searchTargetMessage.id];
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedMessageId(searchTargetMessage.id);
          
          setTimeout(() => {
            setHighlightedMessageId(null);
          }, 3000);
        }
      }, 100);
    }
  }, [searchTargetMessage, messages, onHighlightConsumed]);

  // Notification highlight — scroll to and highlight a specific message (separate from search)
  useEffect(() => {
    if (!highlightMessageId || messages.length === 0) return;

    const targetExists = messages.some(m => m.id === highlightMessageId);

    if (targetExists) {
      // Clear the highlight request immediately (prevents re-triggering on message updates)
      if (onHighlightComplete) onHighlightComplete();

      // Mobile-parity spec: mount the ring first (so the eye lands on an
      // already-glowing bubble), wait 300ms for layout to settle, then
      // smooth-scroll the target to ~40% from the top of the viewport.
      setTimeout(() => {
        const el = messageRefs.current[highlightMessageId];
        if (el) {
          setHighlightedMessageId(highlightMessageId);
          const container = messagesContainerRef.current;
          if (container) {
            const delta = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
            container.scrollTo({
              top: Math.max(container.scrollTop + delta - container.clientHeight * 0.4, 0),
              behavior: 'smooth',
            });
          } else {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          setTimeout(() => setHighlightedMessageId(null), 3000);
        }
      }, 300);
    } else if (hasMore && !loadingMore) {
      loadMoreMessages();
    } else if (!hasMore) {
      if (onHighlightComplete) onHighlightComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightMessageId, messages, hasMore, loadingMore]);

  // Navigate to next search match
  const goToNextMatch = useCallback(() => {
    if (searchMatchIds.length === 0) return;
    
    const newIndex = (navigatedMatchIndex + 1) % searchMatchIds.length;
    setNavigatedMatchIndex(newIndex);
    
    const targetId = searchMatchIds[newIndex];
    const messageElement = messageRefs.current[targetId];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(targetId);
      setTimeout(() => setHighlightedMessageId(null), 3000);
    }
  }, [searchMatchIds, navigatedMatchIndex]);

  // Navigate to previous search match
  const goToPrevMatch = useCallback(() => {
    if (searchMatchIds.length === 0) return;
    
    const newIndex = navigatedMatchIndex === 0 
      ? searchMatchIds.length - 1 
      : navigatedMatchIndex - 1;
    setNavigatedMatchIndex(newIndex);
    
    const targetId = searchMatchIds[newIndex];
    const messageElement = messageRefs.current[targetId];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(targetId);
      setTimeout(() => setHighlightedMessageId(null), 3000);
    }
  }, [searchMatchIds, navigatedMatchIndex]);

  // Check if scrolled to bottom
  const checkIfAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom < SCROLL_BOTTOM_THRESHOLD;
  }, []);

  // Scroll to bottom (for new messages button)
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
    setNewMessageCount(0);
    setShowNewMessageButton(false);
    setIsAtBottom(true);
  }, []);

  // Single scroll effect for the unified message stream.
  // Handles both "user sent a message" and "someone else sent a message".
  // Because pending messages are merged into the array, when onSnapshot confirms
  // a pending message the array length stays the same (swap) → no extra scroll.
  const prevUnifiedCountRef = useRef(0);
  useEffect(() => {
    const currentCount = unifiedMessages.length;
    const prevCount = prevUnifiedCountRef.current;

    if (hasScrolledToBottomRef.current && currentCount > prevCount && prevCount > 0) {
      const newMsgCount = currentCount - prevCount;

      if (isAtBottom) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
            }
          }, 50);
        });
      } else {
        setNewMessageCount(prev => prev + newMsgCount);
        setShowNewMessageButton(true);
      }
    }

    prevUnifiedCountRef.current = currentCount;
  }, [unifiedMessages.length, isAtBottom]);

  // Preserve scroll position when loading older messages
  useEffect(() => {
    if (isLoadingMoreRef.current && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const newScrollHeight = container.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeightRef.current;
      
      if (scrollDiff > 0) {
        container.scrollTop = scrollDiff;
      }
      
      isLoadingMoreRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]); // Only trigger on message count change, not reaction updates

  // Handle scroll events
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Check if at bottom
    const atBottom = checkIfAtBottom();
    setIsAtBottom(atBottom);
    
    // Hide new message button if user scrolled to bottom
    if (atBottom) {
      setNewMessageCount(0);
      setShowNewMessageButton(false);
    }
    
    // Load more when scrolled near top
    if (scrollTop < SCROLL_TOP_THRESHOLD && hasMore && !loading && !loadingMore && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true;
      prevScrollHeightRef.current = scrollHeight;
      loadMoreMessages();
    }
  }, [hasMore, loading, loadingMore, loadMoreMessages, checkIfAtBottom]);

  // Store ref for each message
  const setMessageRef = useCallback((id, element) => {
    if (element) {
      messageRefs.current[id] = element;
    }
  }, []);

  // Empty state - no messages yet (only show when not loading)
  if (!loading && messages.length === 0) {
    return (
      <div data-ui="empty" className="flex-1 flex items-center justify-center" data-testid="empty-messages-state">
        <div className="text-center px-8">
          <div data-ui="empty-glyph" className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center mx-auto mb-5 shadow-inner">
            <MessageSquare className="w-10 h-10 text-blue-400" />
          </div>
          <h3 data-ui="empty-title" className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">No messages yet</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-[260px] mx-auto">
            Send a message to start the conversation. Your messages will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 relative flex flex-col min-h-0">
      {/* Search Navigation Bar */}
      {searchQuery && searchMatchIds.length > 0 && (
        <div 
          data-ui="msg-nav"
          className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20 
                     flex items-center gap-2 bg-card border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 shadow-lg"
          data-testid="search-navigator"
        >
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <Search className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            <span className="font-medium max-w-[120px] truncate">&ldquo;{searchQuery}&rdquo;</span>
          </div>
          
          <div className="w-px h-5 bg-slate-200 dark:bg-[#3e4145]" />
          
          <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap" data-testid="match-counter">
            {navigatedMatchIndex + 1} of {searchMatchIds.length}
          </span>
          
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevMatch}
              disabled={searchMatchIds.length <= 1}
              className="h-7 w-7 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-white/10"
              data-testid="search-prev-btn"
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNextMatch}
              disabled={searchMatchIds.length <= 1}
              className="h-7 w-7 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-white/10"
              data-testid="search-next-btn"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClearSearch}
            className="h-7 w-7 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10"
            data-testid="search-clear-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Scrollable messages container - uses flex-1 for proper height calculation */}
      {/* Mobile: pb accounts for fixed MessageComposer + tab bar overlap (~140px+safe-area) */}
      {/* Desktop: composer is relative (in flow), so minimal padding needed */}
      {/* Mobile: composer is fixed, so bottom padding must match composer + tab bar + safe area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 md:pb-6"
        onScroll={handleScroll}
        data-testid="messages-container"
        style={{
          WebkitOverflowScrolling: 'touch',
          paddingBottom: window.innerWidth < 768
            ? `calc(${Math.max(composerHeight, 60) + 56}px + env(safe-area-inset-bottom))`
            : undefined
        }}
      >
        {/* Load more indicator at top */}
        {hasMore && (
          <div className="flex justify-center py-4">
            {loadingMore ? (
              <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading older messages...</span>
              </div>
            ) : (
              <button 
                onClick={() => {
                  isLoadingMoreRef.current = true;
                  prevScrollHeightRef.current = messagesContainerRef.current?.scrollHeight || 0;
                  loadMoreMessages();
                }}
                data-ui="load-more"
                className="text-sm text-blue-500 dark:text-blue-400 hover:text-blue-600 hover:underline"
                data-testid="load-more-btn"
              >
                Load older messages
              </button>
            )}
          </div>
        )}

        {/* No more messages indicator */}
        {!hasMore && messages.length > 0 && (
          <div className="flex justify-center py-4">
            <span data-ui="begin" className="text-xs text-slate-400 dark:text-slate-500">
              Beginning of conversation
            </span>
          </div>
        )}

        {/* Messages */}
        {unifiedMessages.map((message) => (
          <React.Fragment key={message.id}>
            {/* Date separator */}
            {message.showDateSeparator && (
              <DateSeparator timestamp={message.createdAt} />
            )}
            
            {/* Message bubble with ref for scrolling */}
            <div ref={(el) => setMessageRef(message.id, el)}>
              <MessageBubble
                message={message}
                showHighlightRing={highlightedMessageId === message.id}
                isOwnMessage={message.isOwnMessage}
                isFirstInGroup={message.isFirstInGroup}
                isLastInGroup={message.isLastInGroup}
                showSenderName={message.showSenderName}
                conversationParticipants={conversation?.participants || []}
                participantDetails={conversation?.participantDetails || {}}
                isGroupChat={isGroupChat}
                conversationId={conversation?.id}
                currentUserId={currentUserId}
                currentUser={currentUser}
                highlightText={searchQuery}
                removedParticipants={conversation?.removedParticipants || {}}
                onRetryMessage={message._pending ? onRetryMessage : undefined}
                onCancelMessage={message._pending ? onCancelMessage : undefined}
                onReply={onReply}
              />
            </div>
          </React.Fragment>
        ))}

        {/* Error display */}
        {error && (
          <div className="flex justify-center py-4">
            <span className="text-sm text-red-500 dark:text-red-400">{error}</span>
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* "New messages" floating button */}
      {showNewMessageButton && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-4 left-1/2 transform -translate-x-1/2 
                     bg-blue-500 hover:bg-blue-600 text-white 
                     px-4 py-2 rounded-full shadow-lg 
                     flex items-center gap-2 
                     transition-all duration-200 
                     animate-bounce-subtle
                     z-10"
          data-ui="new-msgs"
          data-testid="new-messages-btn"
        >
          <ChevronDown className="w-4 h-4" />
          <span className="text-sm font-medium">
            {newMessageCount === 1 
              ? 'New message' 
              : `${newMessageCount} new messages`
            }
          </span>
        </button>
      )}
    </div>
  );
}
